import { MINING_DATA } from '../../../data/mining';
import { FARMING_DATA } from '../../../data/farming';
import { CRAFTING_RECIPES } from '../../../data/crafting';
import { UPGRADES } from '../../../data/upgrades';
import type { Resource, UpgradeDef, CraftingRecipe } from '../../../types';

export interface CompendiumItem {
    id: Resource;
    name: string;
    category: 'resource' | 'equipment' | 'farming' | 'other';
    subCategory?: string; // e.g. Ore, Bar, Sword, Seed
    description?: string;
    basePrice?: number; // Base market price if known

    // Metadata
    levelRequirement?: number;
    skillRequirement?: string; // mining, farming, etc.

    // Graph Data
    sources: {
        type: 'mining' | 'farming' | 'crafting' | 'shop';
        detail: string; // e.g. "Copper Vein (Lvl 1)" or "Recipe: Copper Bar"
        id?: string; // ID of the source (recipe ID etc)
        recipe?: Record<string, number>; // Ingredients if crafting
    }[];
    uses: {
        type: 'crafting' | 'upgrade' | 'building';
        detail: string; // e.g. "Copper Bar" or "Upgrade: Better Pickaxe"
        id?: string;
        recipe?: Record<string, number>; // Ingredients if crafting/building
    }[];
}

// Helper to determine category from ID
const categorize = (id: string): { category: CompendiumItem['category']; subCategory: string } => {
    if (id.endsWith('_ore') || ['stone', 'coal', 'obsidian', 'meteorite'].includes(id)) return { category: 'resource', subCategory: 'Ore/Mineral' };
    if (id.endsWith('_bar') || ['copper_wire', 'iron_gear'].includes(id)) return { category: 'resource', subCategory: 'Material' };
    if (['wood'].includes(id)) return { category: 'resource', subCategory: 'Wood' };

    // Farming
    if (FARMING_DATA.some(f => f.id === id)) return { category: 'farming', subCategory: 'Seed' };
    if (['potato', 'carrot', 'corn', 'wheat', 'hops', 'herbs', 'alien_flora', 'crystal_tree'].includes(id)) return { category: 'farming', subCategory: 'Produce' };

    // Equipment
    if (id.includes('_pickaxe') || id.includes('drill') || id.includes('cutter')) return { category: 'equipment', subCategory: 'Tool' };
    if (id.includes('_helmet') || id.includes('_platebody') || id.includes('_platelegs') || id.includes('boots')) return { category: 'equipment', subCategory: 'Armor' };
    if (id.includes('_ring') || id.includes('_necklace')) return { category: 'equipment', subCategory: 'Jewelry' };

    // Food
    if (['bread', 'stew', 'salad', 'roast', 'pie', 'cake'].some(s => id.includes(s)) || ['mashed_potatoes'].includes(id)) return { category: 'resource', subCategory: 'Food' };

    return { category: 'other', subCategory: 'Misc' };
};

export const getCompendiumData = (): CompendiumItem[] => {
    const items = new Map<Resource, CompendiumItem>();

    // Helper to ensure item exists
    const ensureItem = (id: Resource, nameFallback?: string) => {
        if (!items.has(id)) {
            const name = nameFallback || id.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            const { category, subCategory } = categorize(id);
            items.set(id, {
                id,
                name,
                category,
                subCategory,
                sources: [],
                uses: []
            });
        }
        return items.get(id)!;
    };

    // 1. Process Mining
    MINING_DATA.forEach(m => {
        const item = ensureItem(m.id, m.name);
        item.skillRequirement = 'Mining';
        item.levelRequirement = m.level;
        item.sources.push({ type: 'mining', detail: `Mining Level ${m.level}` });
    });

    // 2. Process Farming
    FARMING_DATA.forEach(f => {
        // The Seed
        const seed = ensureItem(f.id, f.name); // Using seed ID as the main item for "Seed"
        seed.skillRequirement = 'Farming';
        seed.levelRequirement = f.level;
        // Wait, FARMING_DATA id is the crop type (e.g. 'potato'), usually used as the seed resource too?
        // In types.ts: 'potato' | ...
        // In plantAction: cost is in Gold.
        // So you buy seeds with gold. The item IS the produce.
        // "Seeds" are abstract? "cost: { gold: 10 }".
        // Ah, farming plots grow the item directly from gold?
        // Let's assume the ITEM is the produce.
        seed.sources.push({ type: 'farming', detail: `Farming Level ${f.level} (Cost: ${f.cost}g)` });
    });

    // 3. Process Crafting (Sources & Uses)
    CRAFTING_RECIPES.forEach(r => {
        // Outputs (Sources) - What materials make THIS item
        Object.keys(r.output).forEach((outId) => {
            const item = ensureItem(outId as Resource);
            item.sources.push({
                type: 'crafting',
                detail: `Crafting: ${r.name} (Lvl ${r.level})`,
                // id: undefined, // Don't make the whole card clickable for sources, let user click ingredients
                recipe: r.input // The ingredients needed to make this
            });
            // Update metadata if it's the primary source
            if (!item.skillRequirement) {
                item.skillRequirement = 'Crafting';
                item.levelRequirement = r.level;
            }
        });

        // Inputs (Uses) - What THIS item is used for
        Object.keys(r.input).forEach((inId) => {
            const item = ensureItem(inId as Resource);
            item.uses.push({
                type: 'crafting',
                detail: `Ingredient: ${r.name}`,
                id: Object.keys(r.output)[0], // The main Output item is what we want to link to
                recipe: r.input // The ingredients for the THING being made (so user sees context)
            });
        });
    });

    // 4. Process Upgrades (Uses)
    UPGRADES.forEach(u => {
        Object.keys(u.cost).forEach((resId) => {
            const item = ensureItem(resId as Resource);
            item.uses.push({
                type: 'upgrade',
                detail: `Upgrade: ${u.label}`,
                // id: undefined, // Upgrades are not items (yet), so not clickable
                recipe: u.cost // Cost of the upgrade
            });
        });
    });

    // 5. Manual Additions for Base Resources
    ensureItem('wood', 'Wood');
    ensureItem('gold', 'Gold');

    return Array.from(items.values());
};
